/**
 * Goal Detail Page - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Light ops mode.
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiClient } from '../api/apiConfig';
import { COLORS, SPACING, TYPOGRAPHY } from '../design/tokens';
import { Screen, Panel, Badge, Divider } from '../design/primitives';

interface Goal {
  id: number;
  title?: string;
  description?: string;
  target_date?: string;
  is_completed?: boolean;
  category?: { id: number; name: string } | null;
  business?: any;
  projects?: any[];
  total_tasks?: number;
  completed_tasks?: number;
  completion_percentage?: number;
  days_remaining?: number;
}

const GoalDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [goal, setGoal] = useState<Goal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGoal = async () => {
      try {
        const response = await apiClient.get(`/api/goals/${id}/`);
        setGoal(response.data);
      } catch (err) {
        setError('Failed to load goal details.');
      } finally {
        setLoading(false);
      }
    };
    fetchGoal();
  }, [id]);

  if (loading) {
    return (
      <Screen>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh'
        }}>
          <p style={{
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.default,
            color: COLORS.text.secondary
          }}>
            Loading...
          </p>
        </div>
      </Screen>
    );
  }

  if (error || !goal) {
    return (
      <Screen>
        <Panel>
          <p style={{
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.default,
            color: COLORS.status.error,
            margin: 0
          }}>
            {error || 'Goal not found.'}
          </p>
        </Panel>
      </Screen>
    );
  }

  return (
    <Screen>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <Panel>
          <h1 style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h1,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            margin: 0,
            marginBottom: SPACING[8]
          }}>
            {goal.title || 'Untitled Goal'}
          </h1>

          <Divider />

          <p style={{
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.default,
            color: COLORS.text.secondary,
            margin: 0,
            marginTop: SPACING[8],
            marginBottom: SPACING[16],
            lineHeight: '1.6',
            minHeight: '1.5rem'
          }}>
            {goal.description ? (
              goal.description
            ) : (
              <span style={{ fontStyle: 'italic', color: COLORS.text.tertiary }}>
                No description provided.
              </span>
            )}
          </p>

          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: SPACING[12],
            alignItems: 'center',
            marginTop: SPACING[16]
          }}>
            {goal.target_date && (
              <Badge color="default">
                Target date: {new Date(goal.target_date).toLocaleDateString()}
              </Badge>
            )}

            <Badge color={goal.is_completed ? 'success' : 'accent'}>
              {goal.is_completed ? 'Completed' : 'Active'}
            </Badge>

            {goal.days_remaining !== undefined && (
              <Badge color="warning">
                {goal.days_remaining} days remaining
              </Badge>
            )}

            {goal.completion_percentage !== undefined && (
              <Badge color="accent">
                {goal.completion_percentage}% complete
              </Badge>
            )}

            {goal.total_tasks !== undefined && (
              <Badge color="default">
                {goal.completed_tasks} / {goal.total_tasks} tasks complete
              </Badge>
            )}
          </div>
        </Panel>
      </div>
    </Screen>
  );
};

export default GoalDetailPage;
